import { InvestorDetailsLegalEntityAssetClassTargetsQuery } from '@/__generated__/graphql';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { TableBodyWrapper, TableRowCell } from '@/components/table/styles';
import { currencyFormatter } from '@/shared/utils/currency';
import { getUTCDate } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import styled from 'styled-components';

const capacityToolTipContent = (
  pacingTarget: InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'][number]['pacingTarget']
) => {
  if (!pacingTarget) return <></>;

  return (
    <TableBodyWrapper>
      <TableRowCell width={100}>
        <TitleCell>Plan Start {getUTCDate(pacingTarget.startDate).toUpperCase()}</TitleCell>
      </TableRowCell>
      <TableRowCell width={100}>
        <TitleCell>Cumulative Plan Goal Since Start: {currencyFormatter.format(pacingTarget.cumulativePacingTarget)}</TitleCell>
      </TableRowCell>
      <TableRowCell width={100}>
        <TitleCell>Commitments Since Start: {currencyFormatter.format(pacingTarget.cumulativeCommitments)}</TitleCell>
      </TableRowCell>
      {pacingTarget.evergreenCapacity && (
        <TableRowCell width={100}>
          <TitleCell>Evergreen Capacity: {currencyFormatter.format(pacingTarget.evergreenCapacity)}</TitleCell>
        </TableRowCell>
      )}
      <TableRowCell width={100}>
        <TitleCell>
          Remaining Pacing Target Through {getUTCDate(new Date(pacingTarget.pacingTargetThroughDate)).toUpperCase()}:{' '}
          {currencyFormatter.format(pacingTarget.remainingPacingTarget)}
        </TitleCell>
      </TableRowCell>
    </TableBodyWrapper>
  );
};

interface SummaryTableCapacityCellProps {
  row:
    | InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'][number]
    | InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'][number]['subAssetClasses'][number];
  bold?: boolean;
  isMobile?: boolean;
  commitmentPlansTotal: number | null;
  targetOCRatio: number | null;
  targetSubAssetClasses?: boolean;
}

const SummaryTableCapacityCell = ({
  row,
  bold = false,
  isMobile = false,
  commitmentPlansTotal,
  targetOCRatio,
  targetSubAssetClasses = false
}: SummaryTableCapacityCellProps) => {
  // // If targeting sub asset classes, and if this is a parent asset class calculate sum of sub asset classes
  // if (targetSubAssetClasses && 'subAssetClasses' in row) {
  //   const subAssetClassesSum = row.subAssetClasses.reduce((sum, subAssetClass) => {
  //     // Use remainingPacingTarget if available, otherwise use capacity
  //     const valueToAdd =
  //       subAssetClass.pacingTarget?.remainingPacingTarget !== undefined ? subAssetClass.pacingTarget.remainingPacingTarget : subAssetClass.capacity;

  //     return sum + (valueToAdd || 0);
  //   }, 0);

  //   return (
  //     <TitleCell bold={bold}>
  //       <FormattingTooltip >{subAssetClassesSum}</FormattingTooltip>
  //     </TitleCell>
  //   );
  // }

  if (commitmentPlansTotal !== null && commitmentPlansTotal > 0 && targetOCRatio === null) {
    return (
      <TitleCell bold={bold}>
        {row.illiquid ? (
          <CellWithTooltip>
            <FormattingTooltip>{row?.capacity}</FormattingTooltip>
            <TooltipWrapper isMobile={isMobile} id="infoWrap">
              <Tooltip title={capacityToolTipContent(row.pacingTarget)}>
                <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
              </Tooltip>
            </TooltipWrapper>
          </CellWithTooltip>
        ) : (
          <CellWithTooltip>
            <FormattingTooltip>{row.capacity}</FormattingTooltip>
            <TooltipWrapper isMobile={isMobile} id="infoWrap">
              {/* Don't show tooltip if targeting sub asset classes and this is a parent asset class */}
              {!targetSubAssetClasses || !('subAssetClasses' in row) ? (
                <Tooltip title="This is an evergreen category and the pacing target reflects the total overall capacity.">
                  <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
                </Tooltip>
              ) : (
                <></>
              )}
            </TooltipWrapper>
          </CellWithTooltip>
        )}
      </TitleCell>
    );
  }

  if (commitmentPlansTotal !== null && commitmentPlansTotal === 0 && targetOCRatio === null) {
    return (
      <TitleCell bold={bold}>
        {row.illiquid ? (
          <CellWithTooltip>
            --
            <TooltipWrapper isMobile={isMobile} id="infoWrap">
              <Tooltip title="Create a commitment plan to see capacity for drawdown asset classes.">
                <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
              </Tooltip>
            </TooltipWrapper>
          </CellWithTooltip>
        ) : (
          <FormattingTooltip>{row.capacity}</FormattingTooltip>
        )}
      </TitleCell>
    );
  }

  return (
    <TitleCell bold={bold}>
      <FormattingTooltip>{row.capacity}</FormattingTooltip>
    </TitleCell>
  );
};

export default SummaryTableCapacityCell;

const CellWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TooltipWrapper = styled.div<{ isMobile: boolean }>`
  opacity: ${({ isMobile }) => (isMobile ? 1 : 0)};
`;

const TitleCell = styled.span<{ color?: string; bold?: boolean }>`
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: ${({ bold }) => (bold ? 600 : 400)};
  line-height: 22px;
  letter-spacing: 0;
  text-align: left;
  color: ${themeVar('font.base')};
`;
